/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { apiTokenService } from '../services/api';
import { isLoopbackRedirect, buildCliRedirect } from '../utils/cliAuthorize';

// CliAuthorize is the browser hand-off target for `podstack auth login`. The
// CLI opens this page with a loopback redirect_uri and a state parameter. We
// require the user to be signed in (reusing the normal Google/GitHub/SSO/OTP
// login), mint a long-lived API token, and redirect the browser back to the
// CLI's local server with the token and state. No psk_ is ever copied by hand.
type Status = 'working' | 'done' | 'error';

const CliAuthorize: React.FC = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const [status, setStatus] = useState<Status>('working');
  const [message, setMessage] = useState('Authorizing the Podstack CLI…');
  const handled = useRef(false);

  const redirectUri = params.get('redirect_uri') || '';
  const state = params.get('state') || '';

  useEffect(() => {
    if (handled.current) return;

    if (!isLoopbackRedirect(redirectUri)) {
      handled.current = true;
      setStatus('error');
      setMessage(
        'Invalid or missing redirect target. Start sign-in from the Podstack CLI with `podstack auth login`.'
      );
      return;
    }

    if (!isAuthenticated) {
      // Sign in first, then return to this page to complete the hand-off.
      // Use the router-relative path (no /portal basename) so finishLogin's
      // '/cli/authorize' match works and navigate() re-adds the basename once.
      sessionStorage.setItem('cli_return', location.pathname + location.search);
      navigate('/login');
      return;
    }

    handled.current = true;
    (async () => {
      try {
        // Rotate: revoke any prior CLI tokens first, so signing in again
        // doesn't pile up tokens and hit the per-user limit. Best-effort.
        try {
          const listRes = await apiTokenService.list();
          const rows: any[] = Array.isArray(listRes.data) ? listRes.data : listRes.data?.data || [];
          await Promise.all(
            rows
              .filter((t) => t && t.id && typeof t.name === 'string' && t.name.startsWith('Podstack CLI'))
              .map((t) => apiTokenService.revoke(t.id).catch(() => undefined))
          );
        } catch {
          // ignore - proceed to create even if listing/revoking failed
        }
        const res = await apiTokenService.create('Podstack CLI', '365d');
        const token: string | undefined = res.data?.token;
        if (!token) {
          throw new Error('no token returned');
        }
        setStatus('done');
        setMessage('Signed in. Returning you to the terminal…');
        window.location.href = buildCliRedirect(redirectUri, token, state);
      } catch (err: any) {
        setStatus('error');
        if (err?.response?.status === 402) {
          setMessage(
            'You have reached the maximum number of API tokens. Revoke one under Settings → API Tokens, then run `podstack auth login` again.'
          );
        } else {
          setMessage('Could not create a CLI token. Please try again from `podstack auth login`.');
        }
      }
    })();
  }, [isAuthenticated, redirectUri, state, navigate, location.pathname, location.search]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-900 text-gray-100 px-4">
      <div className="max-w-md w-full text-center">
        <h1 className="text-xl font-semibold mb-3">Podstack CLI</h1>
        <p className="text-gray-300">{message}</p>
        {status !== 'working' && (
          <p className="mt-4 text-sm text-gray-400">You can close this window and return to the terminal.</p>
        )}
      </div>
    </div>
  );
};

export default CliAuthorize;
